import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useCollegeContext } from '../../contexts/CollegeContext';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { supabase } from '../../lib/supabase';
import { toast } from 'react-hot-toast';
import { formatForDisplay } from '../../utils/valueFormatters';
// import ImportCollegesButton from './ImportCollegesButton'; // Archived
import BrutalistCollegeGrid from './BrutalistCollegeGrid';
import CollegeListView from './CollegeListView';
// import DuplicateCollegeMerger from './DuplicateCollegeMerger'; // Archived
import CollegeDataEditor from './CollegeDataEditor';
import FieldLockManager from './FieldLockManager';
import EssayRequirementsModal from './EssayRequirementsModal';
import SchoolCategorySection from './SchoolCategorySection';
import CollegeSelector from './CollegeSelector';
import CollegeTargetSettings from './CollegeTargetSettings';
import AICollegeRecommendations from './AICollegeRecommendations';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  updateCollegeWithAI, 
  undoAIUpdate, 
  getUpdateHistory,
  isAnyUpdateInProgress 
} from '../../utils/collegeAIUpdate';
import {
  Globe,
  Users,
  UserCheck,
  Plus,
  Search,
  Building,
  MapPin,
  ExternalLink,
  Star,
  Grid,
  List,
  Eye,
  ArrowLeft,
  Save,
  Filter,
  Target,
  TrendingUp,
  BarChart3,
  Calendar,
  CheckCircle,
  Upload,
  Download,
  Settings,
  Zap,
  Brain,
  Edit3,
  Database,
  Check,
  Trophy,
  Heart,
  X,
  Shield,
  Undo,
  Lock,
  Unlock,
  GripVertical,
  Trash2
} from 'lucide-react';

const EnhancedCollegeDataHub = () => {
  // Feature flags
  const ENABLE_COLLEGE_IMPORTS = false; // Set to true to show import functionality
  
  const { currentUser, familyId, familyUsers } = useFamilyContext();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Responsive helper
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const isMobile = windowWidth < 640;
  const isTablet = windowWidth < 768;
  const {
    colleges,
    loadCollegeJourneyData,
    addCollege,
    updateCollege,
    removeCollege,
    lockedCategories,
    toggleCategoryLock,
    customTargets
  } = useCollegeContext();

  // State management
  const isParent = currentUser?.isParent || currentUser?.type === 'parent';
  const [viewMode, setViewMode] = useState(isParent ? 'global' : 'student'); // 'global' or 'student'
  const [selectedStudent, setSelectedStudent] = useState(isParent ? null : currentUser);
  const [globalColleges, setGlobalColleges] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    type: 'all',
    state: 'all',
    category: 'all'
  });
  
  // Smart search state for My Schools view
  const [smartSearchTerm, setSmartSearchTerm] = useState('');
  const [smartSearchResults, setSmartSearchResults] = useState([]);
  const [showSmartSearchDropdown, setShowSmartSearchDropdown] = useState(false);
  const [smartSearchLoading, setSmartSearchLoading] = useState(false);
  
  // Additional modal states
  const [showDuplicateMerger, setShowDuplicateMerger] = useState(false);
  const [showCollegeDataEditor, setShowCollegeDataEditor] = useState(false);
  const [selectedCollegeForEdit, setSelectedCollegeForEdit] = useState(null);
  const [displayMode, setDisplayMode] = useState('grid'); // 'grid' or 'list'
  const [sortBy, setSortBy] = useState('name'); // 'name', 'ranking', 'acceptance_rate', 'tuition', 'enrollment'
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'
  
  // AI Update states
  const [updatingCollegeId, setUpdatingCollegeId] = useState(null);
  const [collegeUpdateHistory, setCollegeUpdateHistory] = useState({});
  const [showFieldLockManager, setShowFieldLockManager] = useState(false);
  const [selectedCollegeForLocks, setSelectedCollegeForLocks] = useState(null);
  
  // Essay Requirements modal state
  const [showEssayRequirementsModal, setShowEssayRequirementsModal] = useState(false);
  const [selectedCollegeForEssays, setSelectedCollegeForEssays] = useState(null);
  
  // Selection and list management state
  const [checkedColleges, setCheckedColleges] = useState(new Set());
  const [showAddToListModal, setShowAddToListModal] = useState(false);
  const [selectedListCategory, setSelectedListCategory] = useState('target');
  const [selectedListStudent, setSelectedListStudent] = useState(null);

  // Drag and drop state
  const draggedCollegeRef = useRef(null); // Use ref for reliable drag data
  const [draggedCollege, setDraggedCollege] = useState(null);
  const [dragOverCategory, setDragOverCategory] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartPos = useRef({ x: 0, y: 0 }); // Track drag start position
  const hasDraggedRef = useRef(false); // Track if actual dragging occurred

  // Return navigation state
  const [returnTo, setReturnTo] = useState(null);

  // Add College Modal state
  const [showAddCollegeModal, setShowAddCollegeModal] = useState(false);
  const [selectedCategoryForAdd, setSelectedCategoryForAdd] = useState('target');
  const [selectedCollege, setSelectedCollege] = useState(null);

  // AI College Recommendations modal state
  const [showAIRecommendations, setShowAIRecommendations] = useState(false);

  // Target Settings Modal state
  const [showTargetSettings, setShowTargetSettings] = useState(false);

  // Get children for student selection
  const children = familyUsers?.filter(user => user.type !== 'parent') || [];

  // Prevent flicker when toggling locks by preserving previous colleges
  const [isTogglingLock, setIsTogglingLock] = useState(false);
  const previousCollegesByCategory = useRef({});

  // Load data on mount
  useEffect(() => {
    if (currentUser && familyId) {
      loadCollegeJourneyData();
      loadGlobalColleges();
    } else if (currentUser) {
      // Load global colleges even without familyId
      loadGlobalColleges();
    }
  }, [currentUser, familyId, loadCollegeJourneyData]);

  // Also load on mount regardless of user state for global data
  useEffect(() => {
    loadGlobalColleges();
  }, []);

  // Handle URL parameters for direct editing and view mode
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const editCollegeId = urlParams.get('edit');
    const returnToParam = urlParams.get('returnTo');
    const viewParam = urlParams.get('view');

    // Handle view parameter - switch between my-schools, all-schools, compare, tier-analysis, portfolio-strategy
    if (viewParam) {
      if (viewParam === 'all-schools' || viewParam === 'global') {
        setViewMode('global');
      } else if (viewParam === 'my-schools' || viewParam === 'student') {
        setViewMode('student');
      } else if (viewParam === 'compare') {
        // Navigate to compare page under colleges wrapper
        navigate('/college/colleges/compare');
        return;
      } else if (viewParam === 'tier-analysis') {
        // Navigate to tier-analysis page under colleges wrapper
        navigate('/college/colleges/tier-analysis');
        return;
      } else if (viewParam === 'portfolio-strategy') {
        // Navigate to portfolio-strategy page under colleges wrapper
        navigate('/college/colleges/portfolio-strategy');
        return;
      }
    }

    // Store the returnTo parameter if provided
    if (returnToParam && !returnTo) {
      setReturnTo(returnToParam);
      console.log('📍 Will return to:', returnToParam);
    }

    if (editCollegeId && colleges.length > 0) {
      // Find the college to edit
      const collegeToEdit = colleges.find(c => c.id === editCollegeId);
      if (collegeToEdit) {
        console.log('🎯 Auto-opening editor for college:', collegeToEdit.name);
        setSelectedCollegeForEdit(collegeToEdit);
        setShowCollegeDataEditor(true);

        // Clean up URL parameters but keep returnTo and view in state
        urlParams.delete('edit');
        urlParams.delete('returnTo');
        const newUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');
        window.history.replaceState({}, '', newUrl);
      }
    }
  }, [location.search, colleges, returnTo, navigate]);

  // Load global colleges
  const loadGlobalColleges = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('global_colleges')
        .select('*')
        .eq('is_active', true)
        .order('name');

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }
      
      setGlobalColleges(data || []);
    } catch (error) {
      console.error('Error loading global colleges:', error);
      toast.error('Failed to load global colleges');
    } finally {
      setLoading(false);
    }
  };

  // Enrich student colleges with global college data for display
  const enrichStudentColleges = (studentColleges) => {
    return studentColleges?.map(college => {
      if (college.global_college_id) {
        const globalCollege = globalColleges.find(gc => gc.id === college.global_college_id);
        if (globalCollege) {
          // Merge global data with student data, keeping student data as priority for personal fields
          return {
            ...globalCollege,  // Rich global data (rankings, academic focus, etc.)
            ...college,        // Student-specific data (category, status, etc.)
            id: college.id,    // Keep student college ID for operations
            user_id: college.user_id,  // Keep student ownership
            family_id: college.family_id,
            global_college_id: college.global_college_id,
            category: college.category,
            status: college.status,
            created_at: college.created_at,
            updated_at: college.updated_at,
            // Prefer global data for display fields, fallback to student data
            name: college.name || globalCollege.name,
            ranking: globalCollege.usnwr_ranking || globalCollege.ranking || college.ranking,
            usnwr_ranking: globalCollege.usnwr_ranking || college.usnwr_ranking,
            engineering_ranking: globalCollege.engineering_ranking || college.engineering_ranking,
            nursing_ranking: globalCollege.nursing_ranking || college.nursing_ranking,
            business_ranking: globalCollege.business_ranking || college.business_ranking,
            acceptance_rate: globalCollege.acceptance_rate || college.acceptance_rate,
            tuition: globalCollege.tuition || college.tuition,
            tuition_in_state: globalCollege.tuition_in_state || college.tuition_in_state,
            tuition_out_of_state: globalCollege.tuition_out_of_state || college.tuition_out_of_state,
            sat_range: globalCollege.sat_range || college.sat_range,
            act_range: globalCollege.act_range || college.act_range,
            test_optional: globalCollege.test_optional !== null ? globalCollege.test_optional : college.test_optional,
            total_cost: globalCollege.total_cost || college.total_cost,
            room_board: globalCollege.room_board || college.room_board
          };
        }
      }
      return college; // Return original if no global data available
    }) || [];
  };

  // Filter and group colleges based on search and filters
  const filteredAndGroupedColleges = useMemo(() => {
    let filteredColleges = [];
    
    if (viewMode === 'global') {
      filteredColleges = globalColleges.filter(college => {
        const matchesSearch = !searchTerm || 
          college.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (college.aliases && college.aliases.some(alias => 
            alias.toLowerCase().includes(searchTerm.toLowerCase())
          ));
        
        const matchesType = filters.type === 'all' || college.type === filters.type;
        const matchesState = filters.state === 'all' || college.state === filters.state;
        const matchesAcademicFocus = !filters.academic_focus || filters.academic_focus === 'all' || 
          (college.academic_focus && college.academic_focus.includes(filters.academic_focus));
        
        return matchesSearch && matchesType && matchesState && matchesAcademicFocus;
      });
      
      return { all: filteredColleges };
    } else {
      // For students, only show their own colleges regardless of selectedStudent
      const studentColleges = !isParent ? 
        colleges.filter(c => c.user_id === currentUser.id) : 
        (selectedStudent ? colleges.filter(c => c.user_id === selectedStudent.id) : colleges);
      
      // Enrich student colleges with global data for rich display
      const enrichedStudentColleges = enrichStudentColleges(studentColleges);
      
      filteredColleges = enrichedStudentColleges.filter(college => {
        const matchesSearch = !searchTerm || 
          college.name.toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesCategory = filters.category === 'all' || college.category === filters.category;
        
        return matchesSearch && matchesCategory;
      });

      // Group by category for student view
      const grouped = {
        reach: [],
        target: [],
        safety: [],
        additional: []
      };
      
      filteredColleges.forEach(college => {
        if (college.category === 'reach' || college.category === 'reach') {
          grouped.reach.push(college);
        } else if (college.category === 'target') {
          grouped.target.push(college);
        } else if (college.category === 'safety') {
          grouped.safety.push(college);
        } else {
          grouped.additional.push(college);
        }
      });
      
      return grouped;
    }
  }, [viewMode, globalColleges, colleges, selectedStudent, searchTerm, filters, isParent, currentUser?.id]);
  
  // For backward compatibility, create a flat array when needed
  const filteredColleges = useMemo(() => {
    if (viewMode === 'global') {
      return filteredAndGroupedColleges.all || [];
    } else {
      const { reach = [], target = [], safety = [], additional = [] } = filteredAndGroupedColleges;
      return [...reach, ...target, ...safety, ...additional];
    }
  }, [filteredAndGroupedColleges, viewMode]);


  // Add global college to student's list
  const addToStudentList = async (globalCollege, student, category = 'other') => {
    try {
      const collegeData = {
        name: globalCollege.name,
        official_name: globalCollege.official_name || globalCollege.name,
        location: globalCollege.location || `${globalCollege.city}, ${globalCollege.state}`,
        website: globalCollege.website,
        global_college_id: globalCollege.id,
        category: category,
        status: 'researching',
        application_round: 'regular'
      };

      await addCollege(collegeData, student.id, familyId);
      // Don't show toast here since quickAddCollege shows its own
      return true;
    } catch (error) {
      console.error('Error adding college to student:', error);
      toast.error('Failed to add college to student list');
      return false;
    }
  };
  
// Add selected colleges to reach/target/safety list
  const addSelectedToList = async () => {
    if (isParent && !selectedListStudent) {
      toast.error('Please select a student');
      return;
    }
    
    // For students, use current user; for parents, use selected student
    const targetStudent = isParent ? selectedListStudent : currentUser;
    if (!targetStudent) {
      toast.error('Unable to determine target student');
      return;
    }
    
    const selectedCollegesList = Array.from(checkedColleges);
    if (selectedCollegesList.length === 0) {
      toast.error('Please select at least one college');
      return;
    }
    
    try {
      setLoading(true);
      let successCount = 0;
      
      for (const collegeId of selectedCollegesList) {
        const college = globalColleges.find(c => c.id === collegeId);
        if (college) {
          await addToStudentList(college, targetStudent, selectedListCategory);
          successCount++;
        }
      }
      
      const listOwner = isParent ? `${targetStudent.displayName || targetStudent.name}'s` : 'your';
      toast.success(`Added ${successCount} colleges to ${listOwner} ${selectedListCategory} list`);
      setCheckedColleges(new Set());
      setShowAddToListModal(false);
    } catch (error) {
      console.error('Error adding colleges to list:', error);
      toast.error('Failed to add colleges to list');
    } finally {
      setLoading(false);
    }
  };

  // Handle sort changes
  const handleSortChange = (newSortBy) => {
    if (sortBy === newSortBy) {
      // Same column, toggle direction
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, start with ascending
      setSortBy(newSortBy);
      setSortDirection('asc');
    }
  };
  
  // AI Update handlers
  const handleAIUpdate = async (college) => {
    // Check if any update is already in progress
    if (isAnyUpdateInProgress()) {
      toast.error('Another update is in progress. Please wait.');
      return;
    }
    
    // Use global college ID for tracking updates
    const globalCollegeId = college.global_college_id || college.id;
    setUpdatingCollegeId(globalCollegeId);
    
    const result = await updateCollegeWithAI(college, {
      onProgress: (isUpdating) => {
        if (!isUpdating) {
          setUpdatingCollegeId(null);
        }
      },
      onSuccess: ({ updatedFields, lockedFields }) => {
        // Reload colleges to show updated data
        if (viewMode === 'global') {
          loadGlobalColleges();
        }
        
        // Update history tracking
        setCollegeUpdateHistory(prev => ({
          ...prev,
          [globalCollegeId]: true
        }));
        
        if (lockedFields > 0) {
          toast.success(`Updated ${college.name} (${lockedFields} fields protected)`);
        }
      },
      onError: (error) => {
        console.error('AI update error:', error);
      }
    });
    
    setUpdatingCollegeId(null);
  };
  
  const handleUndo = async (collegeId) => {
    const result = await undoAIUpdate(collegeId, {
      onSuccess: () => {
        // Reload colleges to show restored data
        if (viewMode === 'global') {
          loadGlobalColleges();
        }
        
        // Clear history tracking for this college
        setCollegeUpdateHistory(prev => {
          const newHistory = { ...prev };
          delete newHistory[collegeId];
          return newHistory;
        });
      }
    });
  };
  
  const handleManageLocks = (college) => {
    setSelectedCollegeForLocks(college);
    setShowFieldLockManager(true);
  };
  
  const handleEssayRequirements = (college) => {
    setSelectedCollegeForEssays(college);
    setShowEssayRequirementsModal(true);
  };
  
  // Check if a college has update history
  const hasUpdateHistory = (collegeId) => {
    const history = getUpdateHistory(collegeId);
    return history !== null;
  };
  
  // Smart search for adding colleges in My Schools view
  useEffect(() => {
    const searchColleges = async () => {
      if (smartSearchTerm.length < 2) {
        setSmartSearchResults([]);
        setShowSmartSearchDropdown(false);
        return;
      }
      
      setSmartSearchLoading(true);
      try {
        const { data, error } = await supabase
          .from('global_colleges')
          .select('id, name, city, state, usnwr_ranking, acceptance_rate')
          .or(`name.ilike.%${smartSearchTerm}%,aliases.cs.{${smartSearchTerm}}`)
          .eq('is_active', true)
          .order('usnwr_ranking', { ascending: true, nullsFirst: false })
          .limit(10);
        
        if (error) throw error;
        
        // Filter out colleges already in student's list
        const studentCollegeIds = colleges
          .filter(c => c.user_id === (selectedStudent?.id || currentUser?.id))
          .map(c => c.global_college_id)
          .filter(Boolean);
        
        const filteredResults = data?.filter(college => 
          !studentCollegeIds.includes(college.id)
        ) || [];
        
        setSmartSearchResults(filteredResults);
        setShowSmartSearchDropdown(filteredResults.length > 0);
      } catch (error) {
        console.error('Error searching colleges:', error);
      } finally {
        setSmartSearchLoading(false);
      }
    };
    
    const debounceTimer = setTimeout(searchColleges, 300);
    return () => clearTimeout(debounceTimer);
  }, [smartSearchTerm, colleges, selectedStudent, currentUser]);
  
  // Quick add college with category
  const quickAddCollege = async (college, category) => {
    const targetStudent = selectedStudent || currentUser;
    if (!targetStudent) {
      toast.error('Please select a student');
      return;
    }
    
    try {
      await addToStudentList(college, targetStudent, category);
      
      // Clear the search
      setSmartSearchTerm('');
      setSmartSearchResults([]);
      setShowSmartSearchDropdown(false);
      
      // Show success with category emoji
      const categoryEmoji = {
        reach: '🎯',
        target: '🎯',
        safety: '🛡️'
      };
      toast.success(`${categoryEmoji[category]} Added ${college.name} to ${category} schools!`);
    } catch (error) {
      console.error('Error adding college:', error);
      toast.error('Failed to add college');
    }
  };

  // Handle adding college from modal
  const handleAddCollegeFromModal = async () => {
    if (!selectedCollege) {
      toast.error('Please select a college');
      return;
    }

    const targetStudent = selectedStudent || currentUser;
    if (!targetStudent) {
      toast.error('Please select a student');
      return;
    }

    try {
      await addToStudentList(selectedCollege, targetStudent, selectedCategoryForAdd);

      // Show success with category emoji
      const categoryEmoji = {
        reach: '🎯',
        target: '🎯',
        safety: '🛡️',
        other: '📚',
        additional: '📚'
      };
      toast.success(`${categoryEmoji[selectedCategoryForAdd] || '✅'} Added ${selectedCollege.name} to ${selectedCategoryForAdd} schools!`);

      // Close modal and reset state
      setShowAddCollegeModal(false);
      setSelectedCollege(null);
      setSelectedCategoryForAdd('target');
    } catch (error) {
      console.error('Error adding college:', error);
      toast.error('Failed to add college');
    }
  };

  // Wrapper for toggleCategoryLock to prevent flicker
  const handleToggleCategoryLock = async (categoryKey, isLocked) => {
    // Store current colleges before toggling
    previousCollegesByCategory.current[categoryKey] = filteredAndGroupedColleges[categoryKey] || [];

    setIsTogglingLock(true);
    await toggleCategoryLock(categoryKey, isLocked);

    // Small delay to ensure state has updated
    setTimeout(() => {
      setIsTogglingLock(false);
    }, 100);
  };

  // Drag and drop handlers with click detection
  const handleDragStart = (e, college) => {
    // Store initial drag position
    dragStartPos.current = { x: e.clientX, y: e.clientY };
    hasDraggedRef.current = false;

    // Always allow the drag to start, but we'll check in handleDragEnd if it was actually a drag
    draggedCollegeRef.current = college;
    setDraggedCollege(college);
    setIsDragging(true);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', college.id);
  };

  const handleDrag = (e) => {
    // Track if user has actually moved the mouse during drag
    if (!hasDraggedRef.current && dragStartPos.current.x && dragStartPos.current.y) {
      const deltaX = Math.abs(e.clientX - dragStartPos.current.x);
      const deltaY = Math.abs(e.clientY - dragStartPos.current.y);
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

      const DRAG_THRESHOLD = 10;
      if (distance > DRAG_THRESHOLD) {
        hasDraggedRef.current = true;
      }
    }
  };

  const handleDragOver = (e, category) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverCategory(category);
  };

  const handleDragLeave = () => {
    setDragOverCategory(null);
  };

  const handleDrop = async (e, newCategory) => {
    e.preventDefault();
    e.stopPropagation();

    const college = draggedCollegeRef.current; // Get from ref

    if (college && college.category !== newCategory) {
      // Don't allow dropping into locked categories
      if (lockedCategories?.[newCategory]) {
        // Visual feedback already shown via SchoolCategorySection overlay
        setDragOverCategory(null);
        return;
      }

      try {
        await updateCollege(college.id, { category: newCategory });
        toast.success(`Moved ${college.name} to ${newCategory}`);
      } catch (error) {
        console.error('Error updating college category:', error);
        toast.error('Failed to move college');
      }
    }

    setDragOverCategory(null);
  };

  const handleDragEnd = () => {
    draggedCollegeRef.current = null; // Clear ref
    setDraggedCollege(null);
    setDragOverCategory(null);
    setIsDragging(false);
  };

  // Handle click on college card
  const handleCollegeCardClick = (e, college) => {
    // Don't navigate if user actually dragged
    if (hasDraggedRef.current) {
      return;
    }

    // Don't navigate if clicking on interactive elements
    const target = e.target;
    if (target.closest('button') || target.closest('select') || target.tagName === 'SELECT') {
      return;
    }

    // Navigate to strategy page
    navigate(`/college/colleges/${college.id}`);
  };

  // Helper function to get round label
  const getRoundLabel = (round) => {
    switch(round) {
      case 'early_decision': return 'ED';
      case 'early_action': return 'EA';
      case 'early_decision_2': return 'ED II';
      case 'regular': return 'RD';
      case 'rolling': return 'Rolling';
      default: return 'RD';
    }
  };

  // Helper function to get round color
  const getRoundColor = (round) => {
    switch(round) {
      case 'early_decision': return '#dc2626'; // red
      case 'early_action': return '#3b82f6'; // blue
      case 'early_decision_2': return '#f59e0b'; // amber
      case 'regular': return '#6b7280'; // gray
      case 'rolling': return '#10b981'; // green
      default: return '#6b7280';
    }
  };

  // Helper function to intelligently parse ranking data
  const parseRanking = (ranking) => {
    if (!ranking) return null;

    // If it's already a number, return it
    if (typeof ranking === 'number') return ranking;

    // If it's a string, try to parse it
    if (typeof ranking === 'string') {
      // Try parsing as JSON first
      try {
        const parsed = JSON.parse(ranking);
        if (parsed.rank) return parsed.rank;
        if (parsed.ranking) return parsed.ranking;
      } catch (e) {
        // Not JSON, try parsing as plain number
        const num = parseInt(ranking, 10);
        if (!isNaN(num)) return num;
      }
    }

    // If it's an object with rank property
    if (typeof ranking === 'object' && ranking.rank) {
      return ranking.rank;
    }

    return null;
  };

  // Enhanced college card rendering with application round selector - Unified Card System
  const renderStudentCollegeCard = (college, categoryKey) => {
    const isBeingDragged = draggedCollege?.id === college.id;

    // Get category colors for Unified Card System
    const getCategoryColors = () => {
      switch(categoryKey) {
        case 'reach':
          return {
            header: 'bg-coral-400',
            border: 'border-coral-800',
            text: 'text-white',
            textSecondary: 'text-white/80'
          };
        case 'target':
          return {
            header: 'bg-gold-400',
            border: 'border-gold-800',
            text: 'text-slate-900',
            textSecondary: 'text-slate-700'
          };
        case 'safety':
          return {
            header: 'bg-teal-500',
            border: 'border-teal-800',
            text: 'text-white',
            textSecondary: 'text-white/80'
          };
        default:
          return {
            header: 'bg-stone-400',
            border: 'border-stone-800',
            text: 'text-white',
            textSecondary: 'text-white/80'
          };
      }
    };

    const colors = getCategoryColors();

    return (
      <div
        key={college.id}
        className={`group relative bg-white border-2 ${colors.border} rounded-lg overflow-hidden transition-all hover:shadow-lg cursor-pointer ${
          isBeingDragged ? 'opacity-50' : ''
        }`}
        draggable
        onDragStart={(e) => handleDragStart(e, college)}
        onDrag={handleDrag}
        onDragEnd={handleDragEnd}
        onClick={(e) => handleCollegeCardClick(e, college)}
      >
        {/* College Name Header with Category Color & Location - Unified Card System */}
        <div
          className={`${colors.header} px-4 py-3 cursor-pointer hover:opacity-90 transition-opacity relative flex items-center justify-between gap-2`}
        >
          <div className="flex-1 min-w-0">
            <h4 className={`font-bold ${colors.text} text-sm`}>{college.name}</h4>
            {college.location && (
              <p className={`${colors.textSecondary} text-xs flex items-center gap-1 mt-1`}>
                <MapPin className="w-3 h-3 flex-shrink-0" />
                <span className="truncate">{college.location}</span>
              </p>
            )}
          </div>

          {/* Edit Icon */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setSelectedCollegeForEdit(college);
              setShowCollegeDataEditor(true);
            }}
            className={`${colors.text} hover:opacity-80 flex-shrink-0 ml-2`}
            title="Edit college"
          >
            <Edit3 className="w-5 h-5" />
          </button>
        </div>

        {/* College Details */}
        <div className="p-4 space-y-3">
          {/* Application Round Selector */}
          <div onClick={(e) => e.stopPropagation()}>
            <label className="text-xs font-medium text-slate-600 block mb-1">
              Application Round
            </label>
            <select
              value={college.application_round || 'regular'}
              onChange={async (e) => {
                e.stopPropagation();
                try {
                  await updateCollege(college.id, {
                    application_round: e.target.value
                  });
                  toast.success(`Updated ${college.name} to ${getRoundLabel(e.target.value)}`);
                } catch (error) {
                  console.error('Error updating application round:', error);
                  toast.error('Failed to update application round');
                }
              }}
              onClick={(e) => e.stopPropagation()}
              className="w-full border-2 border-stone-300 rounded-lg px-3 py-2 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-100 focus:outline-none"
            >
              <option value="early_decision">Early Decision (ED)</option>
              <option value="early_action">Early Action (EA)</option>
              <option value="early_decision_2">Early Decision II</option>
              <option value="regular">Regular Decision</option>
              <option value="rolling">Rolling Admission</option>
            </select>
          </div>

          {/* College Stats */}
          <div className="pt-2 border-t border-stone-200">
            {parseRanking(college.usnwr_ranking) && (
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-slate-600">Ranking:</span>
                <span className="text-sm font-bold text-slate-900">#{parseRanking(college.usnwr_ranking)}</span>
              </div>
            )}
            {college.acceptance_rate && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-600">Acceptance:</span>
                <span className="text-sm font-bold text-slate-900">{formatForDisplay('acceptance_rate', college.acceptance_rate)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Delete Button - Bottom Right on Hover */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (window.confirm(`Remove ${college.name} from your list?`)) {
              removeCollege(college.id);
              toast.success(`Removed ${college.name}`);
            }
          }}
          className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity w-7 h-7 bg-red-500/90 hover:bg-red-600 text-white rounded-full flex items-center justify-center shadow-lg"
          title="Delete college"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  };

  return (
    <div className="space-y-4">

            {/* Main Content */}
            <div className="lg:col-span-2 space-y-4 lg:space-y-6 lg:max-w-7xl mx-auto sm:px-0">
        {/* View Mode Toggle - Refined */}
        <div style={{
          display: 'flex',
          flexDirection: isTablet ? 'column' : 'row',
          alignItems: isTablet ? 'stretch' : 'flex-start',
          justifyContent: isTablet ? 'flex-start' : 'space-between',
          gap: '16px',
          marginBottom: '24px',
          flexWrap: isTablet ? 'nowrap' : 'wrap'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '12px',
            flexWrap: 'wrap',
            minWidth: 0,
            flex: '1 1 auto'
          }}>

          </div>

{viewMode === 'global' && (
            <div style={{ 
              display: 'flex', 
              gap: '8px', 
              flexWrap: 'wrap',
              minWidth: 0,
              justifyContent: 'flex-end',
              alignItems: 'center',
              '@media (max-width: 768px)': {
                justifyContent: 'flex-start',
                width: '100%'
              }
            }}>
              {checkedColleges.size > 0 && (
                <button
                  onClick={() => {
                    if (isParent) {
                      if (children.length === 0) {
                        toast.error('No students found. Please add students first.');
                        return;
                      }
                      setSelectedListStudent(children[0]);
                      setShowAddToListModal(true);
                    } else {
                      // For students, add directly to their own list
                      setSelectedListStudent(currentUser);
                      setShowAddToListModal(true);
                    }
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: isMobile ? '10px 12px' : '12px 20px',
                    background: '#7c3aed',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                    letterSpacing: '0.025em'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.background = '#6d28d9';
                    e.target.style.transform = 'translateY(-1px)';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.background = '#7c3aed';
                    e.target.style.transform = 'translateY(0)';
                  }}
                >
                  <Heart size={16} />
                  Add to {isParent ? 'List' : 'My Schools'} ({checkedColleges.size})
                </button>
              )}
              
              {/* View Toggle Buttons */}
              <div style={{
                display: 'flex',
                background: '#f5f5f4',
                borderRadius: '8px',
                padding: '4px',
                border: '2px solid #e7e5e4'
              }}>
                <button
                  onClick={() => setDisplayMode('grid')}
                  style={{
                    padding: isMobile ? '8px 10px' : '10px 16px',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                    border: 'none',
                    letterSpacing: '0.025em',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    ...(displayMode === 'grid' ? {
                      background: '#0f172a',
                      color: 'white'
                    } : {
                      background: 'transparent',
                      color: '#57534e'
                    })
                  }}
                >
                  <Grid size={14} />
                  {!isMobile && 'Grid'}
                </button>
                <button
                  onClick={() => setDisplayMode('list')}
                  style={{
                    padding: isMobile ? '8px 10px' : '10px 16px',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                    border: 'none',
                    letterSpacing: '0.025em',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    ...(displayMode === 'list' ? {
                      background: '#0f172a',
                      color: 'white'
                    } : {
                      background: 'transparent',
                      color: '#57534e'
                    })
                  }}
                >
                  <List size={14} />
                  {!isMobile && 'List'}
                </button>
              </div>

              {/* Sort Dropdown */}
              <div style={{
                position: 'relative',
                minWidth: isMobile ? '140px' : '180px'
              }}>
                <select
                  value={sortBy}
                  onChange={(e) => {
                    setSortBy(e.target.value);
                    setSortDirection('asc');
                  }}
                  style={{
                    width: '100%',
                    padding: isMobile ? '8px 10px' : '10px 14px',
                    paddingRight: '30px',
                    background: 'white',
                    border: '2px solid #e7e5e4',
                    borderRadius: '8px',
                    fontSize: '13px',
                    fontWeight: 600,
                    color: '#57534e',
                    cursor: 'pointer',
                    appearance: 'none',
                    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                    backgroundPosition: 'right 8px center',
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: '16px'
                  }}
                >
                  <option value="name">Sort by Name</option>
                  <option value="ranking">Sort by National Ranking</option>
                  <option value="engineering_ranking">Sort by Engineering</option>
                  <option value="nursing_ranking">Sort by Nursing</option>
                  <option value="business_ranking">Sort by Business</option>
                  <option value="cs_ranking">Sort by Computer Science</option>
                  <option value="liberal_arts_ranking">Sort by Liberal Arts</option>
                  <option value="acceptance_rate">Sort by Acceptance Rate</option>
                  <option value="tuition">Sort by Tuition</option>
                  <option value="enrollment">Sort by Size</option>
                </select>
              </div>

              {/* Action Buttons for Parents */}
              {(isParent || viewMode === 'global') && (
                <>
                  <button
                    onClick={() => setShowDuplicateMerger(true)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: isMobile ? '10px 12px' : '12px 20px',
                      background: '#dc2626',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                      letterSpacing: '0.025em'
                    }}
                    onMouseOver={(e) => {
                      e.target.style.background = '#b91c1c';
                      e.target.style.transform = 'translateY(-1px)';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.background = '#dc2626';
                      e.target.style.transform = 'translateY(0)';
                    }}
                  >
                    <Database size={16} />
                    {isMobile ? 'Merge' : 'Find Duplicates'}
                  </button>
                  
                  {checkedColleges.size > 0 && (
                    <button
                      onClick={() => {
                        // Get the first selected college and open editor
                        const firstCollegeId = Array.from(checkedColleges)[0];
                        const college = filteredColleges.find(c => c.id === firstCollegeId);
                        if (college) {
                          setSelectedCollegeForEdit(college);
                        }
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: isMobile ? '10px 12px' : '12px 20px',
                        background: '#0d9488',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                        letterSpacing: '0.025em'
                      }}
                      onMouseOver={(e) => {
                        e.target.style.background = '#0f766e';
                        e.target.style.transform = 'translateY(-1px)';
                      }}
                      onMouseOut={(e) => {
                        e.target.style.background = '#0d9488';
                        e.target.style.transform = 'translateY(0)';
                      }}
                    >
                      <Edit3 size={16} />
                      {isMobile ? 'Edit' : checkedColleges.size === 1 ? 'Edit College' : 'Edit First Selected'}
                    </button>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* College Count Summary for My Schools View */}
        {viewMode === 'student' && (
          <div className="mb-6 p-4 sm:p-6 bg-stone-100 rounded-xl border-2 border-slate-900 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
            <div className="grid grid-cols-2 sm:flex sm:justify-around gap-3 sm:gap-6 flex-1">
              <div className="text-center p-3 sm:p-0">
                <div className="text-2xl sm:text-3xl font-bold text-coral-500">
                  {(filteredAndGroupedColleges.reach || []).length}
                </div>
                <div className="text-xs sm:text-sm text-stone-600 font-semibold uppercase mt-1">
                  Reach
                </div>
              </div>
              <div className="text-center p-3 sm:p-0">
                <div className="text-2xl sm:text-3xl font-bold text-gold-500">
                  {(filteredAndGroupedColleges.target || []).length}
                </div>
                <div className="text-xs sm:text-sm text-stone-600 font-semibold uppercase mt-1">
                  Target
                </div>
              </div>
              <div className="text-center p-3 sm:p-0">
                <div className="text-2xl sm:text-3xl font-bold text-teal-500">
                  {(filteredAndGroupedColleges.safety || []).length}
                </div>
                <div className="text-xs sm:text-sm text-stone-600 font-semibold uppercase mt-1">
                  Safety
                </div>
              </div>
              <div className="text-center p-3 sm:p-0">
                <div className="text-2xl sm:text-3xl font-bold text-slate-500">
                  {(filteredAndGroupedColleges.additional || []).length}
                </div>
                <div className="text-xs sm:text-sm text-stone-600 font-semibold uppercase mt-1">
                  Other
                </div>
              </div>
            </div>

            {/* Portfolio Settings Button */}
            <button
              onClick={() => setShowTargetSettings(true)}
              className="flex items-center justify-center gap-2 px-4 sm:px-6 py-3 bg-white text-slate-900 border-2 border-slate-900 rounded-lg text-sm font-semibold hover:bg-slate-900 hover:text-white transition-all whitespace-nowrap"
            >
              <Settings className="w-4 h-4" />
              <span className="hidden sm:inline">Portfolio Settings</span>
              <span className="sm:hidden">Settings</span>
            </button>
          </div>
        )}

        {/* Smart Search Bar for My Schools View */}
        {viewMode === 'student' && (
          <div className="mb-6 flex items-start gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[200px] sm:min-w-[300px] max-w-full sm:max-w-[600px]">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
              <input
                type="text"
                value={smartSearchTerm}
                onChange={(e) => setSmartSearchTerm(e.target.value)}
                placeholder="Search to add colleges to your list..."
                className="w-full pl-12 pr-4 py-3 sm:py-3.5 text-sm sm:text-base border-2 border-stone-200 rounded-xl bg-white outline-none transition-all focus:border-violet-500 focus:ring-4 focus:ring-violet-100"
                onBlur={(e) => {
                  // Delay hiding dropdown to allow clicks
                  setTimeout(() => setShowSmartSearchDropdown(false), 200);
                }}
              />
              
              {/* Search Results Dropdown */}
              {showSmartSearchDropdown && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  marginTop: '8px',
                  background: 'white',
                  border: '2px solid #e5e7eb',
                  borderRadius: '12px',
                  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
                  maxHeight: '400px',
                  overflowY: 'auto',
                  zIndex: 100
                }}>
                  {smartSearchLoading ? (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
                      Searching...
                    </div>
                  ) : smartSearchResults.length > 0 ? (
                    smartSearchResults.map((college) => (
                      <div
                        key={college.id}
                        style={{
                          padding: '12px 16px',
                          borderBottom: '1px solid #f3f4f6',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: '12px'
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <div style={{
                            fontWeight: 600,
                            fontSize: '15px',
                            color: '#111827',
                            marginBottom: '4px'
                          }}>
                            {college.name}
                          </div>
                          <div style={{
                            fontSize: '13px',
                            color: '#6b7280',
                            display: 'flex',
                            gap: '12px'
                          }}>
                            {college.city && college.state && (
                              <span>{college.city}, {college.state}</span>
                            )}
                            {college.usnwr_ranking && (
                              <span>#{college.usnwr_ranking}</span>
                            )}
                            {college.acceptance_rate && (
                              <span>{formatForDisplay('acceptance_rate', college.acceptance_rate)} accept</span>
                            )}
                          </div>
                        </div>
                        
                        <div style={{
                          display: 'flex',
                          gap: '6px'
                        }}>
                          <button
                            onClick={() => quickAddCollege(college, 'reach')}
                            style={{
                              padding: '6px 12px',
                              background: '#dc2626',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '13px',
                              fontWeight: 600,
                              cursor: 'pointer',
                              transition: 'all 0.15s'
                            }}
                            onMouseOver={(e) => e.target.style.background = '#b91c1c'}
                            onMouseOut={(e) => e.target.style.background = '#dc2626'}
                          >
                            + Reach
                          </button>
                          <button
                            onClick={() => quickAddCollege(college, 'target')}
                            style={{
                              padding: '6px 12px',
                              background: '#0d9488',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '13px',
                              fontWeight: 600,
                              cursor: 'pointer',
                              transition: 'all 0.15s'
                            }}
                            onMouseOver={(e) => e.target.style.background = '#0f766e'}
                            onMouseOut={(e) => e.target.style.background = '#0d9488'}
                          >
                            + Target
                          </button>
                          <button
                            onClick={() => quickAddCollege(college, 'safety')}
                            style={{
                              padding: '6px 12px',
                              background: '#3b82f6',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '13px',
                              fontWeight: 600,
                              cursor: 'pointer',
                              transition: 'all 0.15s'
                            }}
                            onMouseOver={(e) => e.target.style.background = '#2563eb'}
                            onMouseOut={(e) => e.target.style.background = '#3b82f6'}
                          >
                            + Safety
                          </button>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
                      No colleges found matching "{smartSearchTerm}"
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* AI College Recommendations Button - Adjacent to search */}
            {!isParent && (
              <button
                onClick={() => setShowAIRecommendations(true)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: isMobile ? '10px 12px' : '14px 20px',
                  background: 'linear-gradient(to right, #0d9488, #14b8a6)',
                  color: 'white',
                  border: '2px solid #0f172a',
                  borderRadius: '12px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  whiteSpace: 'nowrap',
                  alignSelf: 'flex-start'
                }}
                onMouseOver={(e) => {
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
                }}
                onMouseOut={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                }}
              >
                <Brain size={16} />
                Get AI Recommendations
              </button>
            )}
          </div>
        )}

        {/* Import Section - Show for parents only in global view */}
        {/* ImportCollegesButton archived */}
        {/* {ENABLE_COLLEGE_IMPORTS && viewMode === 'global' && isParent && (
          <div style={{ marginBottom: '24px' }}>
            <ImportCollegesButton />
          </div>
        )} */}

        {/* Search and Filters - Only show for Global Colleges view */}
        {viewMode === 'global' && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            flexWrap: 'wrap'
          }}>
            <div style={{ flex: 1, maxWidth: '400px', position: 'relative' }}>
              <Search size={16} style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#a8a29e'
              }} />
              <input
                type="text"
                placeholder="Search colleges..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  paddingLeft: '40px',
                  paddingRight: '16px',
                  paddingTop: '12px',
                  paddingBottom: '12px',
                  border: '2px solid #e7e5e4',
                  borderRadius: '8px',
                  fontSize: '14px',
                  background: '#fafaf9',
                  transition: 'all 0.15s ease'
                }}
              />
            </div>

            <button
              onClick={() => setShowFilters(!showFilters)}
              style={{
                padding: '12px 16px',
                background: showFilters ? '#0f172a' : 'white',
                color: showFilters ? 'white' : '#57534e',
                border: '2px solid #e7e5e4',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'all 0.15s ease'
              }}
            >
              <Filter size={16} />
              Filters
            </button>
          </div>
        )}

        {/* Filters Section */}
        {showFilters && (
          <div style={{
            background: 'white',
            border: '2px solid #e7e5e4',
            borderRadius: '12px',
            padding: '20px',
            marginTop: '16px'
          }}>
            <h3 style={{
              fontSize: '16px',
              fontWeight: 600,
              color: '#0f172a',
              marginBottom: '16px'
            }}>
              Filter Colleges
            </h3>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '16px'
            }}>
              {viewMode === 'global' ? (
                <>
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: 500,
                      color: '#57534e',
                      marginBottom: '6px'
                    }}>
                      Type
                    </label>
                    <select
                      value={filters.type}
                      onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d6d3d1',
                        borderRadius: '6px',
                        fontSize: '14px',
                        background: 'white'
                      }}
                    >
                      <option value="all">All Types</option>
                      <option value="public">Public</option>
                      <option value="private">Private</option>
                      <option value="liberal_arts">Liberal Arts</option>
                    </select>
                  </div>
                  
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: 500,
                      color: '#57534e',
                      marginBottom: '6px'
                    }}>
                      State
                    </label>
                    <select
                      value={filters.state}
                      onChange={(e) => setFilters(prev => ({ ...prev, state: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d6d3d1',
                        borderRadius: '6px',
                        fontSize: '14px',
                        background: 'white'
                      }}
                    >
                      <option value="all">All States</option>
                      {Array.from(new Set(globalColleges.map(c => c.state).filter(Boolean))).sort().map(state => (
                        <option key={state} value={state}>{state}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: 500,
                      color: '#57534e',
                      marginBottom: '6px'
                    }}>
                      Academic Focus
                    </label>
                    <select
                      value={filters.academic_focus || 'all'}
                      onChange={(e) => setFilters(prev => ({ ...prev, academic_focus: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d6d3d1',
                        borderRadius: '6px',
                        fontSize: '14px',
                        background: 'white'
                      }}
                    >
                      <option value="all">All Focus Areas</option>
                      <option value="Engineering">Engineering</option>
                      <option value="Nursing">Nursing</option>
                      <option value="Engineering, Nursing">Both Engineering & Nursing</option>
                    </select>
                  </div>
                </>
              ) : (
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#57534e',
                    marginBottom: '6px'
                  }}>
                    Category
                  </label>
                  <select
                    value={filters.category}
                    onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d6d3d1',
                      borderRadius: '6px',
                      fontSize: '14px',
                      background: 'white'
                    }}
                  >
                    <option value="all">All Categories</option>
                    <option value="reach">Reach</option>
                    <option value="target">Target</option>
                    <option value="safety">Safety</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              )}
              
              <div style={{
                display: 'flex',
                alignItems: 'flex-end'
              }}>
                <button
                  onClick={() => setFilters({
                    type: 'all',
                    state: 'all',
                    category: 'all',
                    academic_focus: 'all'
                  })}
                  style={{
                    padding: '8px 16px',
                    background: '#f5f5f4',
                    border: '1px solid #d6d3d1',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#57534e',
                    cursor: 'pointer'
                  }}
                >
                  Clear Filters
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Colleges Display */}
        {loading ? (
          <div style={{
            textAlign: 'center',
            padding: '40px 20px',
            background: 'white',
            border: '2px solid #e7e5e4',
            borderRadius: '12px',
            marginTop: '24px'
          }}>
            <div style={{
              display: 'inline-block',
              width: '32px',
              height: '32px',
              border: '3px solid #e7e5e4',
              borderTop: '3px solid #0f172a',
              borderRadius: '50%',
              marginBottom: '16px'
            }}></div>
            <p style={{ color: '#57534e', fontSize: '16px', fontWeight: 500 }}>Loading colleges...</p>
          </div>
        ) : filteredColleges.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '60px 20px',
            background: 'white',
            border: '2px solid #e7e5e4',
            borderRadius: '12px',
            marginTop: '24px'
          }}>
            <Building size={48} style={{ color: '#a8a29e', marginBottom: '16px' }} />
            <h3 style={{
              fontSize: '20px',
              fontWeight: 700,
              color: '#0f172a',
              marginBottom: '8px'
            }}>
              No colleges found
            </h3>
            <p style={{
              color: '#57534e',
              fontSize: '16px',
              marginBottom: '20px'
            }}>
              {searchTerm ? 'Try adjusting your search terms' : 'Add some colleges to get started'}
            </p>
          </div>
        ) : displayMode === 'grid' ? (
          viewMode === 'student' ? (
            // Four-section display for student colleges
            <div style={{ marginTop: '24px' }}>
              {/* Reach Schools */}
              <SchoolCategorySection
                title="Reach Schools"
                colleges={isTogglingLock && previousCollegesByCategory.current.reach
                  ? previousCollegesByCategory.current.reach
                  : (filteredAndGroupedColleges.reach || [])}
                categoryKey="reach"
                maxCount={customTargets?.reach || 4}
                onAddSchool={() => {
                  setSelectedCategoryForAdd('reach');
                  setShowAddCollegeModal(true);
                }}
                dragOverCategory={dragOverCategory}
                handleDragOver={handleDragOver}
                handleDragLeave={handleDragLeave}
                handleDrop={handleDrop}
                renderCollegeCard={renderStudentCollegeCard}
                lockedCategories={lockedCategories}
                onToggleLock={handleToggleCategoryLock}
                customTargets={customTargets}
              />

              {/* Target Schools */}
              <SchoolCategorySection
                title="Target Schools"
                colleges={isTogglingLock && previousCollegesByCategory.current.target
                  ? previousCollegesByCategory.current.target
                  : (filteredAndGroupedColleges.target || [])}
                categoryKey="target"
                maxCount={customTargets?.target || 4}
                onAddSchool={() => {
                  setSelectedCategoryForAdd('target');
                  setShowAddCollegeModal(true);
                }}
                dragOverCategory={dragOverCategory}
                handleDragOver={handleDragOver}
                handleDragLeave={handleDragLeave}
                handleDrop={handleDrop}
                renderCollegeCard={renderStudentCollegeCard}
                lockedCategories={lockedCategories}
                onToggleLock={handleToggleCategoryLock}
                customTargets={customTargets}
              />

              {/* Safety Schools */}
              <SchoolCategorySection
                title="Safety Schools"
                colleges={isTogglingLock && previousCollegesByCategory.current.safety
                  ? previousCollegesByCategory.current.safety
                  : (filteredAndGroupedColleges.safety || [])}
                categoryKey="safety"
                maxCount={customTargets?.safety || 4}
                onAddSchool={() => {
                  setSelectedCategoryForAdd('safety');
                  setShowAddCollegeModal(true);
                }}
                dragOverCategory={dragOverCategory}
                handleDragOver={handleDragOver}
                handleDragLeave={handleDragLeave}
                handleDrop={handleDrop}
                renderCollegeCard={renderStudentCollegeCard}
                lockedCategories={lockedCategories}
                onToggleLock={handleToggleCategoryLock}
                customTargets={customTargets}
              />

              {/* Other Schools */}
              <SchoolCategorySection
                title="Other Schools"
                colleges={isTogglingLock && previousCollegesByCategory.current.additional
                  ? previousCollegesByCategory.current.additional
                  : (filteredAndGroupedColleges.additional || [])}
                categoryKey="additional"
                maxCount={10}
                onAddSchool={() => {
                  setSelectedCategoryForAdd('additional');
                  setShowAddCollegeModal(true);
                }}
                dragOverCategory={dragOverCategory}
                handleDragOver={handleDragOver}
                handleDragLeave={handleDragLeave}
                handleDrop={handleDrop}
                renderCollegeCard={renderStudentCollegeCard}
                lockedCategories={lockedCategories}
                onToggleLock={handleToggleCategoryLock}
                customTargets={customTargets}
              />
            </div>
          ) : (
            // Regular grid for global colleges
            <BrutalistCollegeGrid
              colleges={filteredColleges}
              selectedColleges={Array.from(checkedColleges)}
              onSelectionChange={(newSelection) => setCheckedColleges(new Set(newSelection))}
              onCollegeUpdate={updateCollege}
              onCollegeRemove={removeCollege}
              onEditCollege={(college) => {
                setSelectedCollegeForEdit(college);
                setShowCollegeDataEditor(true);
              }}
              onAIUpdate={handleAIUpdate}
              onUndo={handleUndo}
              onManageLocks={handleManageLocks}
              onEssayRequirements={handleEssayRequirements}
              updatingCollegeId={updatingCollegeId}
              showSearch={false}
              showFilters={false}
              viewMode={viewMode}
              isParent={isParent}
              currentUser={currentUser}
              familyUsers={familyUsers}
              sortBy={sortBy}
              sortDirection={sortDirection}
            />
          )
        ) : (
          viewMode === 'student' ? (
            // List view for student colleges - show all categories
            <div style={{ marginTop: '24px' }}>
              <CollegeListView
                colleges={filteredColleges}
                selectedColleges={Array.from(checkedColleges)}
                onSelectCollege={(collegeId) => {
                  const newChecked = new Set(checkedColleges);
                  if (newChecked.has(collegeId)) {
                    newChecked.delete(collegeId);
                  } else {
                    newChecked.add(collegeId);
                  }
                  setCheckedColleges(newChecked);
                }}
                onEditCollege={(college) => {
                  setSelectedCollegeForEdit(college);
                  setShowCollegeDataEditor(true);
                }}
                sortBy={sortBy}
                sortDirection={sortDirection}
                onSortChange={handleSortChange}
                isParent={isParent}
              />
            </div>
          ) : (
            // Regular list for global colleges
            <CollegeListView
              colleges={filteredColleges}
              selectedColleges={Array.from(checkedColleges)}
              onSelectCollege={(collegeId) => {
                const newChecked = new Set(checkedColleges);
                if (newChecked.has(collegeId)) {
                  newChecked.delete(collegeId);
                } else {
                  newChecked.add(collegeId);
                }
                setCheckedColleges(newChecked);
              }}
              onEditCollege={(college) => {
                setSelectedCollegeForEdit(college);
                setShowCollegeDataEditor(true);
              }}
              sortBy={sortBy}
              sortDirection={sortDirection}
              onSortChange={handleSortChange}
              isParent={isParent}
            />
          )
        )}
      </div>
      
      {/* Add to List Modal */}
        {showAddToListModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}>
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '32px',
              maxWidth: '500px',
              width: '100%',
              maxHeight: '80vh',
              overflow: 'auto'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '24px'
              }}>
                <h2 style={{
                  fontSize: '24px',
                  fontWeight: 700,
                  color: '#0f172a'
                }}>
                  Add to College List
                </h2>
                <button
                  onClick={() => setShowAddToListModal(false)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#57534e',
                    cursor: 'pointer',
                    padding: '8px',
                    borderRadius: '6px'
                  }}
                >
                  <X size={20} />
                </button>
              </div>
              
              <div style={{ marginBottom: '20px' }}>
                <p style={{ color: '#57534e', marginBottom: '16px' }}>
                  Adding {checkedColleges.size} college{checkedColleges.size !== 1 ? 's' : ''} to list
                </p>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {isParent && (
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#0f172a',
                    marginBottom: '6px'
                  }}>
                    Select Student
                  </label>
                  <select
                    value={selectedListStudent?.id || ''}
                    onChange={(e) => {
                      const student = children.find(c => c.id === e.target.value);
                      setSelectedListStudent(student || null);
                    }}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '2px solid #e7e5e4',
                      borderRadius: '8px',
                      fontSize: '14px',
                      background: 'white'
                    }}
                  >
                    <option value="">Choose a student...</option>
                    {children.map(child => (
                      <option key={child.id} value={child.id}>
                        {child.displayName || child.name}
                      </option>
                    ))}
                  </select>
                </div>
                )}
                
                {!isParent && (
                  <div style={{
                    padding: '16px',
                    background: '#f0f9ff',
                    border: '1px solid #bae6fd',
                    borderRadius: '8px',
                    color: '#0369a1'
                  }}>
                    Adding to your college list: <strong>{currentUser?.displayName || currentUser?.name || 'My Schools'}</strong>
                  </div>
                )}
                
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#0f172a',
                    marginBottom: '6px'
                  }}>
                    Select List Category
                  </label>
                  <div style={{
                    display: 'flex',
                    gap: '12px',
                    flexWrap: 'wrap'
                  }}>
                    {[
                      { value: 'reach', label: 'Reach', icon: Trophy, color: '#dc2626' },
                      { value: 'target', label: 'Target', icon: Target, color: '#0d9488' },
                      { value: 'safety', label: 'Safety', icon: CheckCircle, color: '#3b82f6' }
                    ].map(category => (
                      <button
                        key={category.value}
                        onClick={() => setSelectedListCategory(category.value)}
                        style={{
                          flex: 1,
                          minWidth: '120px',
                          padding: '16px',
                          border: '2px solid',
                          borderColor: selectedListCategory === category.value ? category.color : '#e7e5e4',
                          borderRadius: '8px',
                          background: selectedListCategory === category.value ? category.color + '10' : 'white',
                          cursor: 'pointer',
                          transition: 'all 0.15s ease',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '8px'
                        }}
                      >
                        <category.icon size={24} style={{ color: category.color }} />
                        <span style={{
                          fontSize: '14px',
                          fontWeight: 600,
                          color: selectedListCategory === category.value ? category.color : '#57534e'
                        }}>
                          {category.label}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '12px',
                marginTop: '24px'
              }}>
                <button
                  onClick={() => setShowAddToListModal(false)}
                  style={{
                    padding: isMobile ? '10px 12px' : '12px 20px',
                    background: 'transparent',
                    color: '#57534e',
                    border: '2px solid #e7e5e4',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={addSelectedToList}
                  disabled={loading || (isParent && !selectedListStudent)}
                  style={{
                    padding: isMobile ? '10px 12px' : '12px 20px',
                    background: loading || (isParent && !selectedListStudent) ? '#94a3b8' : '#7c3aed',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: loading || (isParent && !selectedListStudent) ? 'not-allowed' : 'pointer'
                  }}
                >
                  {loading ? 'Adding...' : `Add to ${isParent ? 'List' : 'My Schools'}`}
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Duplicate College Merger Modal - Archived */}
        {/* {showDuplicateMerger && (
          <DuplicateCollegeMerger
            onClose={() => setShowDuplicateMerger(false)}
            onMergeComplete={() => {
              // Refresh global colleges after merge
              loadGlobalColleges();
              toast.success('Duplicate colleges merged successfully');
            }}
          />
        )} */}
        
        {/* College Data Editor Modal */}
        {selectedCollegeForEdit && (
          <CollegeDataEditor
            college={selectedCollegeForEdit}
            onClose={() => {
              setSelectedCollegeForEdit(null);
              setShowCollegeDataEditor(false);
              
              // If we have a returnTo URL, navigate there after closing
              if (returnTo) {
                console.log('🔙 Returning to:', returnTo);
                setTimeout(() => {
                  navigate(returnTo);
                }, 100);
              }
            }}
            onSave={async (updatedCollege) => {
              try {
                if (viewMode === 'global') {
                  const { error } = await supabase
                    .from('global_colleges')
                    .update(updatedCollege)
                    .eq('id', updatedCollege.id);
                  if (error) throw error;
                  await loadGlobalColleges();
                } else {
                  await updateCollege(updatedCollege.id, updatedCollege);
                  if (loadCollegeJourneyData && typeof loadCollegeJourneyData === 'function') {
                    await loadCollegeJourneyData();
                  }
                }
                toast.success(`Updated ${updatedCollege.name}`);
                // Don't need to close here - the child component calls onClose after successful save
              } catch (error) {
                console.error('Error updating college:', error);
                toast.error('Failed to update college');
                throw error; // Re-throw so the child component knows save failed
              }
            }}
          />
        )}
        
        {/* Field Lock Manager Modal */}
        {showFieldLockManager && selectedCollegeForLocks && (
          <FieldLockManager
            collegeId={selectedCollegeForLocks.global_college_id || selectedCollegeForLocks.id}
            collegeName={selectedCollegeForLocks.name}
            onClose={() => {
              setShowFieldLockManager(false);
              setSelectedCollegeForLocks(null);
            }}
            isModal={true}
          />
        )}
        
        {/* Essay Requirements Modal */}
        {showEssayRequirementsModal && selectedCollegeForEssays && (
          <EssayRequirementsModal
            isOpen={showEssayRequirementsModal}
            onClose={() => {
              setShowEssayRequirementsModal(false);
              setSelectedCollegeForEssays(null);
            }}
            collegeId={selectedCollegeForEssays.id}
            collegeName={selectedCollegeForEssays.name}
          />
        )}

        {/* Add College Modal */}
        {showAddCollegeModal && (
          <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 w-full">
            <div className="bg-white rounded-lg border border-stone-200 p-6 w-full max-w-md shadow-xl">
              <h3 className="text-lg font-bold text-stone-900 mb-6">Add College</h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">Select College</label>
                  <CollegeSelector
                    onCollegeSelect={(college) => setSelectedCollege(college)}
                    onRequestNewCollege={() => {
                      toast.info('Please contact an admin to add a new college to the database');
                    }}
                    placeholder="Search for a college..."
                    selectedCollege={selectedCollege}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">Category</label>
                  <select
                    value={selectedCategoryForAdd}
                    onChange={(e) => setSelectedCategoryForAdd(e.target.value)}
                    className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-azure-500"
                  >
                    <option value="reach">Reach School</option>
                    <option value="target">Target School</option>
                    <option value="safety">Safety School</option>
                    <option value="additional">Other School</option>
                  </select>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowAddCollegeModal(false);
                    setSelectedCollege(null);
                    setSelectedCategoryForAdd('target');
                  }}
                  className="flex-1 px-4 py-2 bg-stone-100 text-stone-700 rounded-lg hover:bg-stone-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddCollegeFromModal}
                  disabled={!selectedCollege}
                  className="flex-1 px-4 py-2 bg-azure-600 text-white rounded-lg hover:bg-azure-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Add College
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Target Settings Modal */}
        <CollegeTargetSettings
          isOpen={showTargetSettings}
          onClose={() => setShowTargetSettings(false)}
        />

        {/* AI College Recommendations Modal */}
        {showAIRecommendations && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
              <div className="flex items-center justify-between p-6 border-b border-slate-200">
                <h2 className="text-lg font-bold text-slate-900">AI College Recommendations</h2>
                <button
                  onClick={() => setShowAIRecommendations(false)}
                  className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="overflow-y-auto max-h-[calc(90vh-80px)]">
                <AICollegeRecommendations
                  userId={currentUser?.id}
                  onClose={() => setShowAIRecommendations(false)}
                />
              </div>
            </div>
          </div>
        )}
      </div>
  );

};

export default EnhancedCollegeDataHub;
